import React, { useCallback, useContext, useState } from "react";

import { saveIndex } from "utils";

import { useRedirect } from "data/hub/hooks";
import { useNotify } from "data/messages/hooks";

import { useBooleanState } from "hooks";
import Access from "components/Access";
import CompactButton from "components/CompactButton";
import { createDroppable, DraggableFragment } from "components/DragDrop";
import { FeatureFlag, useFeatures } from "components/FeatureFlag";
import Spinner from "components/Spinner";
import UseCasesWidget from "components/UseCasesWidget/UseCasesWidget";

import {
  useGetCurrentUser,
  useHubComponentManager,
  useSubscribeToWorkflowTemplateUpdate,
  useView,
  useViewAction,
  useWorkDesk,
  useWorkDeskScreen,
  useWorkSessionExitGuard,
} from "./components/hooks";
import { AccessTypeProvider } from "./components/hooks/useAccessType";
import {
  Provider as ClientWorkflowStore,
  useClientWorkflowFetch,
  useClientWorkflowReducer,
} from "./components/hooks/useClientWorkflows";
import {
  isEmptyFilter,
  Provider as FilterStore,
  useFilterState,
} from "./components/hooks/useFilters";
import { TriggerButton } from "./components/ManyToManyManager/components";
import StartWorkSessionButton from "./components/StartWorkSessionButton";
import {
  CreateWorkflow,
  DragDropHub,
  Filters,
  GettingStarted,
  InsightMenu,
  InsightsBanner,
  InsightsView,
  MenuLayout,
  OnboardingContentWidget,
  SaveView,
  ScreenController,
  ScreenManager,
  ShareResourcesModal,
  StreamManager,
  TagManager,
  useScreenManager,
  Views,
  WithScreenContextProvider,
  WorkDeskExitConfirmationDialog,
  WorkDeskScreen,
  Workflow,
  WorkflowMenu,
  WorkSessionExitGuard,
  WorkspaceLayout,
} from "./components";
import { HubContext, ToasterContext } from "./Provider";

const DroppableWorkflow = createDroppable("Workflows");
export const SCREEN_TYPE = {
  WORKFLOW: "WORKFLOW",
  INSIGHTS: "INSIGHTS",
  WORK_DESK: "WORK_DESK",
};

export function WorkspaceContainer() {
  const { openWorkDesk, exitWorkDesk, shouldShowWorkDesk } = useWorkDesk();
  const {
    showWorkDeskScreen,
    isWorkDeskScreenInFront,
    workSessionCommand,
    prefetchWorkSession,
  } = useWorkDeskScreen();

  const workDeskButton = isWorkDeskScreenInFront
    ? {
        content: "Exit Work Session",
        icon: "sign-out",
        color: "orange",
        tooltip: "Return to your workspace",
        onClick: exitWorkDesk,
        // no intent prefetch while a session is running
        onHoverIntent: undefined,
      }
    : {
        content: "Start Work Session",
        icon: "play",
        color: "blue",
        tooltip: "Your prioritized work session across workflows",
        onClick: () => {
          // entry is the fallback intent for non-hover input
          prefetchWorkSession();
          openWorkDesk();
        },
        // hovering signals intent: warm a fresh session so the click is fast
        onHoverIntent: prefetchWorkSession,
      };

  const {
    interceptWorkSessionExit,
    exitConfirmOpen,
    stayInSession,
    confirmExit,
  } = useWorkSessionExitGuard({
    active: isWorkDeskScreenInFront,
    atWorkDeskRoute: shouldShowWorkDesk,
    onExit: exitWorkDesk,
  });

  const notify = useNotify("WORKFLOWS");
  const redirect = useRedirect();
  const { fireSuccess, fireError } = useContext(ToasterContext);
  const { moveClientWorkflow, moveClientTileToTop } = useContext(HubContext);
  const [allCwsFetch, fetchDispatch] = useClientWorkflowFetch();
  const [cwData, cwDispatch] = useClientWorkflowReducer();
  const [activeInsight, setInsight] = useState();
  const [current, dispatch] = useFilterState();
  const [
    isShareResourcesModalOpen,
    openShareResourcesModal,
    closeShareResourcesModal,
  ] = useBooleanState(false);

  const {
    loading: loadingView,
    currentView,
    fetchView,
    clearView,
    updateCurrentView,
  } = useView({ updateFilter: dispatch });
  const { updateDefaultView } = useViewAction(() => {
    fireSuccess("Your default view has been updated.");
  });
  const manager = useScreenManager();
  const { showButton, showWidget, showWorkspace } = useHubComponentManager({
    workflowLength: current.workflows.length,
    isViewActive: Boolean(currentView?.id),
    isViewLoading: loadingView,
    activeScreen: manager.front(),
  });
  const { user, loadingUser, reloadUser } = useGetCurrentUser();
  const sharedResourcesFlag = useFeatures("shared-resources");

  useSubscribeToWorkflowTemplateUpdate(
    useCallback(
      (workflows) => {
        clearView();
        updateDefaultView();
        dispatch({ type: "workflows", value: workflows });
      },
      [clearView, updateDefaultView, dispatch]
    )
  );

  const handleMovedIntoWorkflow = async (cwId, wfId, des) => {
    try {
      const { id: newCwfId } = await moveClientWorkflow(cwId, wfId);
      await moveClientTileToTop(newCwfId, cwData[wfId][des]?.id);
      fetchDispatch({ type: "set", value: { workflowId: wfId } });
    } catch (error) {
      fireError(
        error?.response?.data?.[0] || "Failed to add client to workflow"
      );
    }
  };

  const activeWorkflows = ({ filterEmptyWorkflows }) => {
    if (filterEmptyWorkflows) {
      return ({ numActiveClientWorkflows }) => {
        return numActiveClientWorkflows > 0;
      };
    }

    return () => {
      return true;
    };
  };

  return (
    <FilterStore value={[current, dispatch]}>
      <WorkspaceLayout
        header={
          <MenuLayout>
            <MenuLayout.Left>
              <StartWorkSessionButton button={workDeskButton} />
              <WorkSessionExitGuard onIntercept={interceptWorkSessionExit}>
                <Views
                  loading={loadingView}
                  disabled={loadingView}
                  selectedView={currentView}
                  onUpdateView={(updatedView) => {
                    if (currentView?.id === updatedView.id) {
                      updateCurrentView(updatedView);
                    }
                  }}
                  onDeselectView={() => {
                    updateDefaultView();
                    clearView();
                  }}
                  onSelectView={(view) => {
                    updateDefaultView(view.id);
                    fetchView(view.id);
                  }}
                />
                <WorkflowMenu
                  selectedWorkflows={current.workflows}
                  onChange={({ workflows, filters, showAll }) => {
                    dispatch({ type: "workflows", value: workflows });

                    if (showAll !== undefined) {
                      dispatch({ type: "showAll", value: showAll });
                    }

                    if (filters) dispatch({ type: "filters", value: filters });
                  }}
                  selectedFilters={current.filters}
                />
                {showButton.filters && (
                  <Filters
                    selectedFilters={current.filters}
                    onChange={(f) => {
                      dispatch({ type: "filters", value: f });
                    }}
                    highlight={!isEmptyFilter(current.filters)}
                  />
                )}
                {showButton.clearAll && (
                  <CompactButton
                    data-test="hub-menu-clear-all"
                    disabled={
                      isEmptyFilter(current.filters) &&
                      current.workflows.length === 0
                    }
                    onClick={() => {
                      if (currentView) updateDefaultView();
                      clearView();
                    }}
                  >
                    Clear All
                  </CompactButton>
                )}
                {showButton.createOrSaveView && (
                  <SaveView
                    selectedWorkflows={current.workflows}
                    filters={current.filters}
                    showAll={current.showAll}
                    currentView={currentView}
                    onUpdateView={updateCurrentView}
                    onCreateView={(view) => {
                      updateDefaultView(view.id);
                      fetchView(view.id);
                    }}
                  />
                )}
              </WorkSessionExitGuard>
            </MenuLayout.Left>
            <MenuLayout.Right>
              <WorkSessionExitGuard onIntercept={interceptWorkSessionExit}>
                {sharedResourcesFlag?.canShare && (
                  <Access required={["Administrator"]}>
                    <div style={{ flex: "0 1 auto", margin: "0 0.5em 0 0" }}>
                      <TriggerButton
                        data-test="hub-menu-share-button"
                        icon={sharedResourcesFlag?.workspace?.icon || "share"}
                        showGearIcon={Boolean(
                          sharedResourcesFlag?.workspace?.iconCorner
                        )}
                        iconCorner={sharedResourcesFlag?.workspace?.iconCorner}
                        text={
                          sharedResourcesFlag?.workspace?.buttonText || "Share"
                        }
                        onClick={() => {
                          if (isShareResourcesModalOpen) {
                            closeShareResourcesModal();
                          } else {
                            if (manager.front() !== SCREEN_TYPE.WORKFLOW) {
                              manager.promote(SCREEN_TYPE.WORKFLOW);
                            }

                            openShareResourcesModal();
                          }
                        }}
                      />
                    </div>
                  </Access>
                )}
                <TagManager
                  onModifyClient={() => {
                    notify(
                      current.workflows.map(({ id }) => {
                        return id;
                      })
                    );
                  }}
                  onUpdate={(tag) => {
                    dispatch({
                      type: "update-tag-filter",
                      value: { type: "tags", data: tag },
                    });
                  }}
                  onDelete={(tagId) => {
                    dispatch({
                      type: "remove-tag-filter",
                      value: { type: "tags", data: { id: tagId } },
                    });
                  }}
                />
                <StreamManager
                  onModifyClient={() => {
                    notify(
                      current.workflows.map(({ id }) => {
                        return id;
                      })
                    );
                  }}
                  onUpdate={(stream) => {
                    dispatch({
                      type: "update-stream-filter",
                      value: { type: "streams", data: stream },
                    });
                  }}
                  onDelete={(streamId) => {
                    dispatch({
                      type: "remove-stream-filter",
                      value: { type: "streams", data: { id: streamId } },
                    });
                  }}
                />
                <FeatureFlag id="insights-upgrade-plan.showInsightsMenu">
                  <InsightMenu
                    onClick={(r) => {
                      setInsight(r);
                      if (!user.insightsEnabled) reloadUser();
                    }}
                    user={user}
                    loading={loadingUser}
                  />
                </FeatureFlag>
                <FeatureFlag
                  id="insights-upgrade-plan.showInsightsMenu"
                  showOnDisabled
                >
                  {user.insightsEnabled && (
                    <InsightMenu onClick={setInsight} user={user} />
                  )}
                </FeatureFlag>
                <CreateWorkflow
                  onCreate={(newWf) => {
                    dispatch({
                      type: "add-workflow",
                      value: { ...newWf, editMode: true },
                    });
                  }}
                  onCreateFromTemplate={() => {
                    return redirect("templates/all");
                  }}
                />
              </WorkSessionExitGuard>
            </MenuLayout.Right>
          </MenuLayout>
        }
      >
        <ScreenManager
          style={{ height: "calc(100vh - 47px - 50px)", width: "100%" }}
        >
          {showWorkDeskScreen && (
            <ScreenController
              registerAtBottom
              data-test="work-desk-screen"
              id={SCREEN_TYPE.WORK_DESK}
              style={{
                overflow: "hidden",
                // bleed over the workflow-container's 1em right padding so
                // the work desk reaches the viewport edge like the overlay did
                width: "calc(100% + 1em)",
              }}
            >
              <WorkDeskScreen
                visible={isWorkDeskScreenInFront}
                sessionCommand={workSessionCommand}
              />
            </ScreenController>
          )}
          <ScreenController
            data-test="insights-dashboard"
            id={SCREEN_TYPE.INSIGHTS}
            style={{ background: "#f7f8fa" }}
          >
            {activeInsight && activeInsight?.dashboard && (
              <InsightsBanner user={user}>
                <InsightsView id={activeInsight.dashboard} />
              </InsightsBanner>
            )}
          </ScreenController>
          <ScreenController
            data-test="hubly-workspace"
            id={SCREEN_TYPE.WORKFLOW}
            style={{ background: "white" }}
          >
            {!showWorkspace && (
              <GettingStarted>
                {showWidget.loader && (
                  <Spinner active size="large">
                    Loading your view
                  </Spinner>
                )}
                {showWidget.useCases && <UseCasesWidget />}
                {showWidget.onboardingContent && <OnboardingContentWidget />}
              </GettingStarted>
            )}
            {showWorkspace && (
              <ClientWorkflowStore
                value={[
                  [cwData, cwDispatch],
                  [allCwsFetch, fetchDispatch],
                ]}
              >
                <DragDropHub
                  onTileMovedIntoWorkflow={handleMovedIntoWorkflow}
                  onWorkflowPositionChanged={(src, des) => {
                    dispatch({ type: "swap-workflows", value: { src, des } });
                  }}
                  onTilePositionChanged={async (cwId, wfId, src, des) => {
                    try {
                      cwDispatch({
                        type: "swap-cw",
                        value: { wfId, src, des },
                      });

                      const beforeItem = src > des ? des : des + 1;
                      await moveClientTileToTop(
                        cwId,
                        cwData[wfId][beforeItem]?.id
                      );
                    } catch {
                      fireError("Failed to move client workflows");
                    }
                  }}
                >
                  <DroppableWorkflow
                    id="Workflow-drop-space"
                    direction="horizontal"
                    style={{ height: "inherit", overflowX: "scroll" }}
                  >
                    <div style={{ paddingLeft: "1em", display: "flex" }}>
                      {current.workflows
                        .map(saveIndex("idx"))
                        .filter(activeWorkflows(current.filters))
                        .map((w) => {
                          return (
                            <DraggableFragment
                              id={w.id}
                              key={w.id}
                              index={w.idx}
                            >
                              <AccessTypeProvider
                                initState={{
                                  ready: true,
                                  readOnly: !w.canEdit,
                                }}
                              >
                                <Workflow
                                  workflow={w}
                                  filters={current.filters}
                                  onHide={() => {
                                    dispatch({
                                      type: "remove-workflow",
                                      value: w,
                                    });
                                  }}
                                  onDuplicated={(newWf) => {
                                    dispatch({
                                      type: "add-workflow",
                                      value: newWf,
                                    });
                                  }}
                                />
                              </AccessTypeProvider>
                            </DraggableFragment>
                          );
                        })}
                      <div style={{ minWidth: "60px" }} />
                    </div>
                  </DroppableWorkflow>
                </DragDropHub>
              </ClientWorkflowStore>
            )}
          </ScreenController>
        </ScreenManager>
      </WorkspaceLayout>

      {isShareResourcesModalOpen && (
        <ShareResourcesModal
          onClose={closeShareResourcesModal}
          onSubmit={closeShareResourcesModal}
        />
      )}

      <WorkDeskExitConfirmationDialog
        open={exitConfirmOpen}
        onStay={stayInSession}
        onExit={confirmExit}
      />
    </FilterStore>
  );
}

WorkspaceContainer.propTypes = {};

WorkspaceContainer.defaultProps = {};

export default WithScreenContextProvider(WorkspaceContainer);
